import { useState } from 'react';
import { Button, Modal, Upload, message } from 'antd';
import { DeleteOutlined, UploadOutlined } from '@ant-design/icons';
import type { UploadProps } from 'antd';
import ImageCropModal from '../../components/ImageCropModal';

interface ActivityLogoUploadProps {
    value?: string;
    onChange?: (value: string) => void;
    confirmRemove?: boolean;
}

const LOGO_HINT = '300 x 300, not exceeding 300 KB';

function isPreviewableLogo(value?: string): boolean {
    return Boolean(value) && value !== 'remove';
}

const ActivityLogoUpload: React.FC<ActivityLogoUploadProps> = ({
    value,
    onChange,
    confirmRemove = false,
}) => {
    const [messageApi, contextHolder] = message.useMessage();
    const [cropModalVisible, setCropModalVisible] = useState(false);
    const [pendingImageFile, setPendingImageFile] = useState<File | null>(null);

    const applyLogo = (nextValue: string) => {
        onChange?.(nextValue);
    };

    const beforeUpload: UploadProps['beforeUpload'] = (file) => {
        const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png';
        if (!isJpgOrPng) {
            messageApi.error('You can only upload JPG/PNG file!');
            return Upload.LIST_IGNORE;
        }
        if (file.size / 1024 / 1024 >= 2) {
            messageApi.error('Image must smaller than 2MB!');
            return Upload.LIST_IGNORE;
        }

        const img = new Image();
        const objectUrl = URL.createObjectURL(file);
        const revokePreview = () => URL.revokeObjectURL(objectUrl);
        img.onload = () => {
            revokePreview();
            const sizeInKB = file.size / 1024;
            if (sizeInKB > 300) {
                messageApi.error(`File size is ${sizeInKB.toFixed(1)}KB, please use an image smaller than 300KB`);
                return;
            }
            if (img.naturalWidth > 300 || img.naturalHeight > 300) {
                setPendingImageFile(file);
                setCropModalVisible(true);
                return;
            }
            const reader = new FileReader();
            reader.onload = () => {
                applyLogo(reader.result as string);
            };
            reader.readAsDataURL(file);
        };
        img.onerror = revokePreview;
        img.src = objectUrl;
        return false;
    };

    const clearLogo = () => {
        applyLogo(value?.startsWith('data:') || !confirmRemove ? '' : 'remove');
    };

    const handleRemove = () => {
        if (!confirmRemove) {
            clearLogo();
            messageApi.success('Logo removed successfully');
            return;
        }
        Modal.confirm({
            title: 'Confirm Delete',
            content: 'Are you sure you want to remove this logo?',
            okText: 'Yes',
            cancelText: 'No',
            onOk: () => {
                clearLogo();
                messageApi.success('Logo removed successfully');
            },
        });
    };

    return (
        <>
            {contextHolder}
            <Upload
                name="logo"
                listType="picture"
                className="logo-uploader"
                beforeUpload={beforeUpload}
                maxCount={1}
                showUploadList={false}
                accept="image/*"
            >
                <Button icon={<UploadOutlined />}>Upload</Button>
            </Upload>
            {isPreviewableLogo(value) && (
                <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <img
                        src={value}
                        alt="logo"
                        style={{ width: '100px', maxWidth: '100px', maxHeight: '100px' }}
                    />
                    <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={handleRemove}
                        title="Remove logo"
                    />
                </div>
            )}
            <div style={{ fontSize: '12px', color: '#999', marginTop: 8 }}>{LOGO_HINT}</div>
            {pendingImageFile && (
                <ImageCropModal
                    visible={cropModalVisible}
                    imageFile={pendingImageFile}
                    onCancel={() => {
                        setCropModalVisible(false);
                        setPendingImageFile(null);
                    }}
                    onConfirm={(croppedImageBase64) => {
                        applyLogo(croppedImageBase64);
                        setCropModalVisible(false);
                        setPendingImageFile(null);
                    }}
                />
            )}
        </>
    );
};

export default ActivityLogoUpload;
